/**
 * Visit types a person can be assigned, from the clinical titles they hold.
 *
 * Mirrors `app/services/clinical_titles.py`. The backend stays authoritative for
 * what is actually stored -- it recomputes this from the submitted titles and
 * ignores any client-supplied discipline list -- but the dialog needs the answer
 * as the operator ticks boxes, and a visit-type list that lags a checkbox is
 * worse than one that is instantly right.
 *
 * A shared fixture (`backend/tests/fixtures/title_discipline_cases.json`) is
 * asserted by tests on both sides, so the two maps cannot drift apart quietly.
 */

import type { DisciplineType } from '../services/api';
import type { UserType } from '../services/api/models/UserType';

/**
 * A title grants exactly the discipline it licenses. Assistant titles map to the
 * discipline they assist with -- a PTA is scheduled on PT visits.
 */
const TITLE_DISCIPLINES: Partial<Record<UserType, DisciplineType>> = {
  RN: 'SN',
  LPN_LVN: 'SN',
  PT: 'PT',
  PTA: 'PT',
  OT: 'OT',
  COTA: 'OT',
  ST: 'ST',
  MSW: 'MSW',
  HHA: 'HHA',
  CNA: 'HHA',
};

/**
 * Titles that license no visit type. Listed rather than inferred from absence,
 * so a title added without deciding its discipline shows up as a test failure
 * instead of silently granting nothing.
 */
const TITLES_WITHOUT_DISCIPLINE: readonly UserType[] = ['CHAPLAIN', 'OTHER'];

/** Order results by discipline so two equal title sets compare equal. */
const DISCIPLINE_ORDER: readonly DisciplineType[] = [
  'PT',
  'OT',
  'ST',
  'SN',
  'MSW',
  'HHA',
];

export function disciplinesForTitles(
  titles: readonly UserType[] | null | undefined
): DisciplineType[] {
  if (!titles?.length) return [];

  const granted = new Set<DisciplineType>();
  for (const title of titles) {
    const discipline = TITLE_DISCIPLINES[title];
    if (discipline) granted.add(discipline);
  }
  return DISCIPLINE_ORDER.filter(discipline => granted.has(discipline));
}

/** The given titles that contribute no visit type, so the dialog can say which. */
export function titlesWithoutDiscipline(
  titles: readonly UserType[] | null | undefined
): UserType[] {
  if (!titles?.length) return [];
  return titles.filter(title => TITLES_WITHOUT_DISCIPLINE.includes(title));
}

/** Titles in neither map -- always empty, asserted by a test. */
export function unclassifiedTitles(allTitles: readonly UserType[]): UserType[] {
  return allTitles.filter(
    title =>
      !(title in TITLE_DISCIPLINES) &&
      !TITLES_WITHOUT_DISCIPLINE.includes(title)
  );
}
